import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Tooltip from '../components/Tooltip';

// GH-175: click "Failed: N" to reveal the top 5 error reasons + counts.
// Kept as a lightweight inline dropdown to avoid pulling in a modal system.
function FailureBreakdown({ reasons }) {
  const [open, setOpen] = React.useState(false);
  return (
    <span style={{ marginLeft: 6 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'transparent', border: 'none', color: '#2563eb',
          cursor: 'pointer', fontSize: '0.85rem', padding: 0, textDecoration: 'underline',
        }}
        aria-expanded={open}
      >
        {open ? 'hide reasons' : 'why?'}
      </button>
      {open && (
        <div style={{
          marginTop: 4, padding: '6px 8px', background: '#f9fafb',
          border: '1px solid #e5e7eb', borderRadius: 4, fontSize: '0.8rem',
        }}>
          {reasons.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 0' }}>
              <span style={{ fontVariantNumeric: 'tabular-nums', color: '#6b7280', minWidth: 28 }}>{r.count}×</span>
              <span style={{ color: '#374151' }}>{r.reason}</span>
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

// commons #227 — Appointments filled via Newton Health vs manually.
function AppointmentSourceReport({ practiceId }) {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 86400000);
  const iso = (d) => d.toISOString().slice(0, 10);
  const [filters, setFilters] = useState({ startDate: iso(monthAgo), endDate: iso(today), providerId: '', appointmentType: '', status: 'completed' });
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [showRows, setShowRows] = useState(false);

  const load = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await api.getAppointmentSourceReport(practiceId, filters);
      setReport(res.data);
    } catch (e) { setErr(e.message || 'Failed to load'); } finally { setBusy(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  const types = report ? report.by_type.map((b) => b.key) : [];
  const badge = (s) => s === 'newton' ? 'badge success' : s === 'manual' ? 'badge warning' : s === 'push_failed' ? 'badge danger' : 'badge info';
  const label = (s) => s === 'newton' ? 'Filled via Newton Health' : s === 'manual' ? 'Filled manually' : s === 'push_failed' ? 'Completed, push failed' : 'Partial';

  return (
    <div className="card">
      <h2>Appointments Filled via Newton Health vs. Manually <Tooltip text="Counts ONLY appointments whose type requires paperwork — types with no intake mapping never generate a form, so including them would score every follow-up as 'filled manually' for having nothing to fill. Newton = form 100% complete and pushed to the EHR. Completed, push failed = the patient finished but our push did not land. Partial = started, not finished. Manual = never started. 'Booked via' shows booking-origin signals (waitlist, voice) independent of intake." /></h2>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end', marginBottom: 12 }}>
        <label style={{ fontSize: '0.85rem' }}>From<br /><input type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} /></label>
        <label style={{ fontSize: '0.85rem' }}>To<br /><input type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} /></label>
        <label style={{ fontSize: '0.85rem' }}>Appointment type<br />
          <input list="appt-source-types" value={filters.appointmentType} placeholder="All" onChange={(e) => setFilters({ ...filters, appointmentType: e.target.value })} />
          <datalist id="appt-source-types">{types.map((t) => <option key={t} value={t} />)}</datalist>
        </label>
        <label style={{ fontSize: '0.85rem' }}>Status<br />
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="completed">Completed only</option>
            <option value="all">All statuses</option>
          </select>
        </label>
        <button className="btn btn-primary" onClick={load} disabled={busy}>{busy ? 'Loading…' : 'Apply'}</button>
      </div>
      {err && <p style={{ color: '#b91c1c' }}>{err}</p>}
      {report && (
        <>
          <div className="stats-grid">
            <div className="stat-card"><h3>Appointments</h3><div className="value">{report.totals.total}</div></div>
            <div className="stat-card"><h3>Filled via Newton Health</h3><div className="value">{report.totals.newton} <small>({report.totals.newton_pct}%)</small></div></div>
            <div className="stat-card"><h3>Filled manually</h3><div className="value">{report.totals.manual} <small>({report.totals.manual_pct}%)</small></div></div>
            <div className="stat-card"><h3>Partial</h3><div className="value">{report.totals.partial} <small>({report.totals.partial_pct}%)</small></div></div>
            <div className="stat-card"><h3>Completed, push failed <Tooltip text="The patient completed the form but the EHR push did not land. Counted separately because this is our failure, not a patient who filled in paper." /></h3><div className="value">{report.totals.push_failed} <small>({report.totals.push_failed_pct}%)</small></div></div>
            <div className="stat-card"><h3>Booked via Newton <Tooltip text="Waitlist backfill or voice booking — independent of intake status" /></h3><div className="value">{report.totals.booked_via_newton}</div></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
            {[['By provider', report.by_provider], ['By appointment type', report.by_type]].map(([title, list]) => (
              <div key={title}>
                <h3>{title}</h3>
                <table className="table">
                  <thead><tr><th>{title.replace('By ', '')}</th><th>Total</th><th>Newton</th><th>Manual</th><th>Partial</th><th>Push failed</th></tr></thead>
                  <tbody>
                    {list.length ? list.map((b) => (
                      <tr key={b.key}><td>{b.key}</td><td>{b.total}</td><td><span className="badge success">{b.newton}</span></td><td><span className="badge warning">{b.manual}</span></td><td><span className="badge info">{b.partial}</span></td><td><span className="badge danger">{b.push_failed}</span></td></tr>
                    )) : <tr><td colSpan="6" style={{ textAlign: 'center', color: '#6c757d' }}>No appointments requiring paperwork in range.</td></tr>}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowRows((s) => !s)}>{showRows ? 'Hide appointments' : `Show appointments (${report.rows.length})`}</button>
            {showRows && (
              <table className="table" style={{ marginTop: 8 }}>
                <thead><tr><th>Date</th><th>Patient</th><th>Provider</th><th>Type</th><th>Status</th><th>Intake</th><th>EHR push</th><th>Booked via</th><th>Source</th></tr></thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.id}>
                      <td>{String(r.appointment_date).slice(0, 10)}</td>
                      <td>{r.patient_first_name} {r.patient_last_name}</td>
                      <td>{r.provider_name || '—'}</td>
                      <td>{r.appointment_type || '—'}</td>
                      <td>{r.status}</td>
                      <td>{Math.round(r.intake_pct)}%</td>
                      <td>{r.ehr_pushed ? (r.ehr_push_status === 'completed' ? 'Yes' : 'Partial') : '—'}</td>
                      <td>{r.booked_via || '—'}</td>
                      <td><span className={badge(r.source)}>{label(r.source)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Reports({ practiceId }) {
  const [performance, setPerformance] = useState([]);
  const [messagingStats, setMessagingStats] = useState({});
  const [optOutReport, setOptOutReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  const loadReports = async () => {
    // The effect fires on mount, and on the first render practiceId is still null
    // because PracticeContext has not resolved the selected practice yet. Without
    // this guard the three calls below went out with no practice_id at all. The
    // server now scopes them to the caller's own practice, so nothing leaks today,
    // but before that fix landed on 2026-09-10 an unscoped campaign-performance or
    // messaging-stats call returned figures across every practice the query touched.
    // The same guard is already used on AiInsights, AiQueue and AiConfig.
    if (!practiceId) return;
    try {
      setLoading(true);
      const [perfRes, msgRes, optOutRes] = await Promise.all([
        api.getCampaignPerformance(null, practiceId),
        api.getMessagingStats(practiceId),
        api.getOptOutReport(practiceId),
      ]);
      setPerformance(perfRes.data);
      setMessagingStats(msgRes.data);
      setOptOutReport(optOutRes.data);
    } catch (err) {
      console.error('Error loading reports:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading reports...</div>;
  }

  return (
    <div className="reports">
      <AppointmentSourceReport practiceId={practiceId} />
      <div className="card">
        <h2>Campaign Performance Report</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Total Enrollments <Tooltip text="Total patients enrolled in this campaign" /></th>
              <th>Active <Tooltip text="Patients currently in the campaign outreach sequence" /></th>
              <th>Completed <Tooltip text="Patients who successfully booked or responded" /></th>
              <th>Exited <Tooltip text="Patients who exited the campaign before completion" /></th>
              <th>Unsubscribed <Tooltip text="Patients who opted out of this campaign" /></th>
              <th>Conversion Rate <Tooltip text="Percentage of enrollments that completed successfully" /></th>
              <th>Avg Retries <Tooltip text="Average number of contact attempts per patient" /></th>
              <th>Revenue <Tooltip text="Recovered revenue / Estimated potential revenue" /></th>
            </tr>
          </thead>
          <tbody>
            {performance.length > 0 ? (
              performance.map((campaign) => (
                <tr key={campaign.campaign_type}>
                  <td style={{ textTransform: 'capitalize', fontWeight: 600 }}>
                    {campaign.campaign_type}
                  </td>
                  <td>{campaign.total_enrollments}</td>
                  <td>
                    <span className="badge info">{campaign.active}</span>
                  </td>
                  <td>
                    <span className="badge success">{campaign.completed}</span>
                  </td>
                  <td>
                    <span className="badge warning">{campaign.exited}</span>
                  </td>
                  <td>
                    <span className="badge danger">{campaign.unsubscribed}</span>
                  </td>
                  <td>{campaign.conversion_rate}%</td>
                  <td>{parseFloat(campaign.avg_retries || 0).toFixed(1)}</td>
                  <td>
                    <div>
                      <strong>${parseFloat(campaign.recovered_revenue || 0).toFixed(2)}</strong>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                      of ${parseFloat(campaign.estimated_revenue || 0).toFixed(2)}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                  No performance data available yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Messaging Statistics</h2>
        {Object.keys(messagingStats).length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {Object.entries(messagingStats).map(([campaignType, channels]) => (
              <div key={campaignType} style={{ border: '1px solid #e1e8ed', borderRadius: '8px', padding: '15px' }}>
                <h3 style={{ textTransform: 'capitalize', marginBottom: '15px' }}>
                  {campaignType}
                </h3>
                <div>
                  <h4>SMS</h4>
                  {channels.sms?.total > 0 ? (
                    <div style={{ marginBottom: '10px' }}>
                      <div>Total: {channels.sms.total}</div>
                      <div>Sent: {channels.sms.sent || 0}</div>
                      <div>Failed: {channels.sms.failed || 0}
                        {channels.sms.failed > 0 && channels.sms.failure_reasons?.length > 0 && (
                          <FailureBreakdown reasons={channels.sms.failure_reasons} />
                        )}
                      </div>
                      <div>Success Rate: {channels.sms.success_rate || 0}%</div>
                    </div>
                  ) : (
                    <div style={{ marginBottom: '10px', color: '#9ca3af' }}>No SMS data</div>
                  )}
                  <h4 style={{ marginTop: '15px' }}>Email</h4>
                  {channels.email?.total > 0 ? (
                    <div>
                      <div>Total: {channels.email.total}</div>
                      <div>Sent: {channels.email.sent || 0}</div>
                      <div>Failed: {channels.email.failed || 0}
                        {channels.email.failed > 0 && channels.email.failure_reasons?.length > 0 && (
                          <FailureBreakdown reasons={channels.email.failure_reasons} />
                        )}
                      </div>
                      <div>Success Rate: {channels.email.success_rate || 0}%</div>
                    </div>
                  ) : (
                    <div style={{ color: '#9ca3af' }}>No email data</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            No messaging statistics available yet.
          </p>
        )}
      </div>

      <div className="card">
        <h2>Opt-Out Report</h2>
        {optOutReport ? (
          <div>
            <div className="stats-grid" style={{ marginBottom: '20px' }}>
              <div className="stat-card orange">
                <h3>Total Opted Out <Tooltip text="Total patients who opted out of all communications" /></h3>
                <div className="value">{optOutReport.total_opted_out}</div>
              </div>
              <div className="stat-card">
                <h3>Opt-Out Rate <Tooltip text="Percentage of all patients who opted out" /></h3>
                <div className="value">{optOutReport.opt_out_rate}%</div>
              </div>
            </div>
            {optOutReport.by_date && optOutReport.by_date.length > 0 && (
              <div>
                <h3>Recent Opt-Outs</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {optOutReport.by_date.map((item, index) => (
                      <tr key={index}>
                        <td>{new Date(item.opt_out_date).toLocaleDateString()}</td>
                        <td>{item.total_opt_outs}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <p style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
            No opt-out data available.
          </p>
        )}
      </div>
    </div>
  );
}

export default Reports;
